import { useMemo, useCallback } from "react";
import { ImageLoader } from "next/image";
import { StoryblokAsset } from "../lib/storyblok/types/fieldtypes/storyblokAsset";
import {
  getStoryblokCropURI,
  storyblokLoader,
} from "../components/StoryblokImage";
import { deduceDomain } from "../lib/utils/deduceDomain";

interface UseSeoImageOptions {
  seoImage?: StoryblokAsset | any; // @eslint-disable-line @typescript-eslint/no-explicit-any
  locale: "en" | "zh" | "ja" | "sv" | "de" | "fr" | "es";
  site: "investor" | "main";
}

interface UseSeoImageReturn {
  seoImageUrl: string;
  seoContentImage: string;
  seoFallbackImage: string;
  isStoryblokAsset: boolean;
  loader: ImageLoader;
}

export const useSeoImage = ({
  seoImage,
  locale,
  site,
}: UseSeoImageOptions): UseSeoImageReturn => {
  const seoFallbackImage = `${deduceDomain(
    locale,
    site
  )}/images/tobii-seo-fallback.jpg`;

  const isStoryblokAsset = useMemo(() => {
    return !!(
      seoImage &&
      "filename" in seoImage &&
      "fieldtype" in seoImage &&
      seoImage.filename &&
      typeof seoImage.filename === "string"
    );
  }, [seoImage]);

  const storyblokAsset = useMemo(() => {
    return isStoryblokAsset ? (seoImage as StoryblokAsset) : undefined;
  }, [isStoryblokAsset, seoImage]);

  const seoImageUrl = useMemo(() => {
    if (!storyblokAsset?.filename) return "";

    const cropData = storyblokAsset.meta_data?.crop;
    const cropURI = getStoryblokCropURI(cropData);
    return storyblokAsset.filename + cropURI;
  }, [storyblokAsset]);

  const loader: ImageLoader = useCallback(({ src, width, quality }) => {
    return storyblokLoader({ src, width, quality });
  }, []);

  const seoContentImage = useMemo(() => {
    if (!seoImageUrl) return seoFallbackImage;

    return loader({
      src: seoImageUrl,
      width: 1200,
      quality: 100,
    });
  }, [loader, seoImageUrl, seoFallbackImage]);

  return {
    seoImageUrl,
    seoContentImage,
    seoFallbackImage,
    isStoryblokAsset,
    loader,
  };
};
